import { bytesToBase64 } from './escpos'

/**
 * Hands a print job to RawBT, the Android print-service app that talks to the
 * receipt printer (Wi-Fi port 9100, Bluetooth or USB). The browser can't open a
 * socket to the printer itself; RawBT can.
 *
 * Two ways in, tried in this order:
 *
 * 1. RawBT's WebSocket server on the tablet (ws://127.0.0.1:40213/). Silent, and
 *    works without a tap, so it is what auto-print needs. On recent RawBT
 *    versions the server comes with RawBT's separate network add-on.
 * 2. An Android intent URL (`intent:base64,…;scheme=rawbt`). Always there with
 *    RawBT installed. Chrome may refuse to open an app without a tap, so it is
 *    reliable for the Print button and best-effort for auto-print.
 */

export const RAWBT_WS_URL = 'ws://127.0.0.1:40213/'
const RAWBT_PACKAGE = 'ru.a402d.rawbtprinter'
const WS_TIMEOUT_MS = 2_000

export type RawBtRoute = 'websocket' | 'intent'

export function isAndroid(userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): boolean {
  return /android/i.test(userAgent)
}

export function rawBtIntentUrl(job: Uint8Array): string {
  return `intent:base64,${bytesToBase64(job)}#Intent;scheme=rawbt;package=${RAWBT_PACKAGE};end;`
}

/** Sends the job over RawBT's WebSocket; rejects when nothing listens there. */
export function sendViaRawBtSocket(
  job: Uint8Array,
  { url = RAWBT_WS_URL, timeoutMs = WS_TIMEOUT_MS }: { url?: string; timeoutMs?: number } = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    let socket: WebSocket
    try {
      socket = new WebSocket(url)
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)))
      return
    }
    socket.binaryType = 'arraybuffer'
    let settled = false
    const finish = (err?: Error) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (err) {
        try {
          socket.close()
        } catch {
          // already closed
        }
        reject(err)
      } else {
        resolve()
      }
    }
    const timer = setTimeout(() => finish(new Error('RawBT did not answer on its WebSocket')), timeoutMs)
    socket.onerror = () => finish(new Error('RawBT WebSocket is not running on this device'))
    socket.onopen = () => {
      try {
        socket.send(job)
        // Give the bytes a moment to leave before closing.
        setTimeout(() => {
          try {
            socket.close(1000, 'Work complete')
          } catch {
            // ignore
          }
          finish()
        }, 200)
      } catch (err) {
        finish(err instanceof Error ? err : new Error(String(err)))
      }
    }
  })
}

/** Opens RawBT with the job through an Android intent. */
export function sendViaRawBtIntent(job: Uint8Array, navigate: (url: string) => void = (url) => {
  window.location.href = url
}): void {
  navigate(rawBtIntentUrl(job))
}

/**
 * The WebSocket first, then the intent. Returns the route that took the job.
 * The intent can't report back, so "intent" means "handed to Android".
 */
export async function sendToRawBt(
  job: Uint8Array,
  deps: {
    socket?: (job: Uint8Array) => Promise<void>
    intent?: (job: Uint8Array) => void
    android?: boolean
  } = {},
): Promise<RawBtRoute> {
  const socket = deps.socket ?? ((j: Uint8Array) => sendViaRawBtSocket(j))
  const intent = deps.intent ?? ((j: Uint8Array) => sendViaRawBtIntent(j))
  try {
    await socket(job)
    return 'websocket'
  } catch (socketError) {
    if (!(deps.android ?? isAndroid())) {
      console.warn('RawBT WebSocket not reachable', socketError)
      throw new Error('RawBT was not found. Receipt printing works on the Android tablet with RawBT installed.')
    }
    intent(job)
    return 'intent'
  }
}
